import { apiClient } from '@/lib/apiClient';

export interface ConsultationSummary {
  id: string;
  appointmentId: string;
  doctorId: string;
  doctorName: string | null;
  summary: string | null;
  interventionCount: number;
  totalAmount: number;
  paidAmount: number;
  completedAt: string | null;
  createdAt: string;
}

export interface Intervention {
  id: string;
  treatmentCatalogId: string;
  toothNumber: number | null;
  surface: string | null;
  price: number;
  status: string;
  notes: string | null;
  /** Set only when someone other than the consultation's doctor did this work. */
  performedByDoctorId: string | null;
  performedByName: string | null;
}

export interface Consultation {
  id: string;
  appointmentId: string;
  patientId: string;
  doctorId: string;
  doctorName: string | null;
  notes: string | null;
  chiefComplaint: string | null;
  examination: string | null;
  recommendations: string | null;
  diagnosis: string | null; // JSON array of ICD-10 codes
  toothChartJson: string | null;
  /** Work done — completed interventions only. The only figure ever owed. */
  totalAmount: number;
  /** Work proposed — planned and in-progress. An estimate, never a debt. */
  quotedAmount: number;
  paidAmount: number;
  completedAt: string | null;
  closedBySystem: boolean;
  createdAt: string;
  updatedAt: string;
  interventions: Intervention[];
}

export interface DictationResult {
  sessionId: string;
  transcript: string;
  extractedFieldsJson: string;
  tokensUsed: number;
}

export const listPatientConsultations = async (patientId: string) => {
  const { data } = await apiClient.get<{ data: ConsultationSummary[] }>(`/patients/${patientId}/consultations`);
  return data.data;
};

/** Start (or open, if already started) the consultation for an appointment. Returns its id. */
export const startConsultation = async (appointmentId: string) => {
  const { data } = await apiClient.post<{ id: string }>('/consultations', { appointmentId });
  return data.id;
};

/** Finalise the consultation: marks it completed (read-only) + the appointment completed. */
export const completeConsultation = (id: string) => apiClient.post(`/consultations/${id}/complete`);

/** Reopen a completed consultation for correction (editable again; appointment back to checked-in). */
export const reopenConsultation = (id: string) => apiClient.post(`/consultations/${id}/reopen`);

/** AI nurse: spoken-style clinical briefing of the patient (no PII sent to the model). */
export const generateConsultationSummary = async (id: string) => {
  const { data } = await apiClient.post<{ summary: string; tokensUsed: number }>(`/consultations/${id}/ai/summary`);
  return data.summary;
};

/** AI nurse: synthesise natural spoken audio (MP3) for a text — the nurse's real voice. */
export const synthesizeSpeech = async (text: string): Promise<Blob> => {
  const { data } = await apiClient.post('/consultations/ai/speech', { text }, { responseType: 'blob' });
  return data as Blob;
};

export const getConsultation = async (id: string) => {
  const { data } = await apiClient.get<Consultation>(`/consultations/${id}`);
  return data;
};

export const updateConsultation = (id: string, body: {
  notes: string | null; chiefComplaint: string | null; examination: string | null;
  recommendations: string | null; diagnosis: string | null; toothChartJson: string | null;
}) => apiClient.put(`/consultations/${id}`, body);

/** Update one intervention (used to flip its status, e.g. Planned → Completed). */
export const updateIntervention = (consultationId: string, interventionId: string, body: {
  toothNumber: number | null; surface: string | null; price: number; status: string; notes: string | null;
  performedByDoctorId?: string | null;
}) => apiClient.put(`/consultations/${consultationId}/interventions/${interventionId}`, body);

export const addIntervention = (id: string, body: {
  treatmentCatalogId: string; toothNumber: number | null; surface: string | null; price: number | null; status: string; notes: string | null;
  performedByDoctorId?: string | null;
}) => apiClient.post(`/consultations/${id}/interventions`, body);

export const removeIntervention = (id: string, interventionId: string) =>
  apiClient.delete(`/consultations/${id}/interventions/${interventionId}`);

export interface ConsultationMaterial { itemName: string; unit: string; quantity: number; }

/** Stock auto-consumed by this consultation's completed interventions (§7.15). */
export const getConsultationMaterials = async (id: string) => {
  const { data } = await apiClient.get<{ data: ConsultationMaterial[] }>(`/consultations/${id}/materials`);
  return data.data;
};

/** Fetch a consultation PDF document and trigger a browser download. */
const downloadConsultationPdf = async (id: string, kind: 'treatment-offer' | 'recommendation') => {
  const res = await apiClient.get(`/consultations/${id}/${kind}`, { responseType: 'blob' });
  const href = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = `${kind}-${id}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
};

export const downloadTreatmentOffer = (id: string) => downloadConsultationPdf(id, 'treatment-offer');
export const downloadRecommendation = (id: string) => downloadConsultationPdf(id, 'recommendation');

/** Fetch a consultation PDF and open it in a new tab for viewing (not a forced download). */
const openConsultationPdf = async (id: string, kind: 'treatment-offer' | 'recommendation') => {
  const res = await apiClient.get(`/consultations/${id}/${kind}`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000); // give the new tab time to load the blob
};

export const openTreatmentOffer = (id: string) => openConsultationPdf(id, 'treatment-offer');
export const openRecommendation = (id: string) => openConsultationPdf(id, 'recommendation');

/** Fetch a consultation PDF and send it straight to the printer. */
const printConsultationPdf = async (id: string, kind: 'treatment-offer' | 'recommendation') => {
  const res = await apiClient.get(`/consultations/${id}/${kind}`, { responseType: 'blob' });
  const { printBlob } = await import('@/lib/print');
  printBlob(res.data as Blob);
};

export const printTreatmentOffer = (id: string) => printConsultationPdf(id, 'treatment-offer');
export const printRecommendation = (id: string) => printConsultationPdf(id, 'recommendation');

/** AI: draft the recommendation letter body (post-op care, meds, follow-up) for the doctor to edit. */
export const draftRecommendations = async (id: string) => {
  const { data } = await apiClient.post<{ recommendations: string; tokensUsed: number }>(`/consultations/${id}/ai/recommendations`);
  return data.recommendations;
};

/** AI agentic action: draft the recommendation letter and save it onto the consultation. */
export const applyRecommendationDraft = (id: string) =>
  apiClient.post(`/consultations/${id}/ai/recommendations/apply`);

/** Uploads a dictation recording and returns the transcript + extracted fields. */
export const transcribeDictation = async (id: string, audio: Blob, durationSeconds: number) => {
  const form = new FormData();
  form.append('audio', audio, 'dictation.webm');
  form.append('durationSeconds', String(durationSeconds));
  const { data } = await apiClient.post<DictationResult>(`/consultations/${id}/dictation/transcribe`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};
